<template>
  <div class="bg-white rounded-2xl p-4 md:p-8 shadow-sm max-w-5xl mx-auto mt-4">
    <div v-if="siteData.cryptoFeatureAvailable">
      <div
        v-if="!loading"
        class="mb-6 bg-gray-50 rounded-xl px-6 py-4 flex flex-col"
      >
        <span class="text-xs text-gray-500 font-medium uppercase"
          >Total Portfolio Value</span
        >
        <span class="text-2xl font-bold text-gray-900 mt-1">
          ${{ totalPortfolioValue.toLocaleString() }}
        </span>
      </div>
      <div class="mb-6">
        <span class="text-blue-700 font-semibold text-base cursor-pointer"
          >All Assets</span
        >
      </div>
      <div class="mb-6">
        <input
          v-model="search"
          type="text"
          placeholder="Search Assets"
          class="w-full rounded-md border-2 border-solid border-yellow-300 focus:border-yellow-400 focus:ring-0 px-4 py-3 text-gray-700 placeholder-gray-400 outline-none transition"
        />
      </div>
      <div
        v-if="searchedCurrency.length == 0 && !loading"
        class="flex flex-col items-center justify-center py-12"
      >
        <div class="text-lg font-semibold text-gray-700">Card Not Found</div>
        <div class="text-gray-500 mt-2">
          <b>"{{ search }}"</b> is not available at the moment
        </div>
      </div>
      <div v-else class="space-y-3">
        <div
          v-for="card in searchedCurrency"
          :key="card.coinId"
          class="flex items-center justify-between bg-gray-50 rounded-xl px-6 py-4 shadow-sm hover:shadow-md transition cursor-pointer border border-transparent hover:border-yellow-200"
          @click="
            $router.push(
              card.hasAutoProcessing
                ? `/user/products/crypto/auto-trade/${card.coinId}`
                : `/user/products/crypto/trade/${card.coinId}`
            )
          "
        >
          <div class="flex items-center gap-4">
            <img
              :src="card.logo"
              :alt="card.currency"
              class="w-10 h-10 rounded-full bg-white border"
            />
            <div>
              <div class="font-semibold text-gray-900 text-base">
                {{ card.currency }}
              </div>
              <div class="text-xs text-gray-500 font-medium uppercase">
                {{ card.symbol }}
              </div>
              <div
                v-if="card.walletBalance"
                class="text-xs text-gray-500 mt-1"
              >
                Balance: {{ card.walletBalance }} {{ card.symbol }}
              </div>
            </div>
          </div>
          <div class="text-right min-w-[90px]">
            <div class="font-semibold text-gray-900 text-base">
              {{ card.price ? `$${Number(card.price).toLocaleString()}` : '-' }}
            </div>
            <div
              v-if="card.walletBalance"
              class="text-xs text-gray-600 font-medium"
            >
              ${{ (Number(card.walletBalance) * Number(card.price || 0)).toLocaleString() }}
            </div>
            <div
              :class="{
                'text-green-500':
                  card.percentageChange && card.percentageChange > 0,
                'text-red-400':
                  card.percentageChange && card.percentageChange < 0,
                'text-gray-400': !card.percentageChange
              }"
              class="text-xs font-medium"
            >
              <span v-if="card.percentageChange > 0"
                >{{ card.percentageChange }}%</span
              >
              <span v-else-if="card.percentageChange < 0"
                >{{ card.percentageChange }}%</span
              >
              <span v-else>0.00%</span>
            </div>
          </div>
        </div>
      </div>
      <div
        v-if="loading"
        class="fixed inset-0 bg-white bg-opacity-60 flex items-center justify-center z-50"
      >
        <svg
          class="animate-spin h-10 w-10 text-yellow-400"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
        >
          <circle
            class="opacity-25"
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            stroke-width="4"
          ></circle>
          <path
            class="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8v8z"
          ></path>
        </svg>
      </div>
    </div>
    <div v-else>
      <div class="bg-gray-50 rounded-xl p-8 text-center text-gray-600">
        {{ siteData.cryptoFeatureMessage }}
      </div>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import cryptoApi from '../../../api/users/cryptocoin'

export default {
  data: () => ({
    search: '',
    currencies: [],
    loading: false
  }),
  created() {
    this.$store.dispatch('user/getSiteSettings')
    this.getCryptos()
    this.$store.dispatch('user/getCountryCurrency')
  },
  computed: {
    ...mapState({
      siteData: (state) => state.user.siteSettings
    }),
    searchedCurrency() {
      if (this.search) {
        return this.currencies.filter(
          (currency) =>
            currency.currency
              .toString()
              .toLowerCase()
              .includes(this.search.toLowerCase()) ||
            currency.symbol
              .toString()
              .toLowerCase()
              .includes(this.search.toLowerCase())
        )
      }
      return this.currencies
    },
    totalPortfolioValue() {
      return this.currencies.reduce((total, card) => {
        const balance = Number(card.walletBalance) || 0
        const price = Number(card.price) || 0
        return total + balance * price
      }, 0)
    }
  },
  methods: {
    async getCryptos() {
      this.loading = true
      const [ratesRes, walletsRes] = await Promise.all([
        cryptoApi.cryptoCoin().getCryptosRate(),
        cryptoApi.cryptoCoin().getCryptosWallets()
      ])
      const wallets = walletsRes.data.data || []
      this.currencies = (ratesRes.data.data || []).map((rate) => {
        const wallet = wallets.find((w) => w.coin._id === rate.coinId)
        return {
          ...rate,
          hasAutoProcessing: wallet?.coin?.hasAutoProcessing ?? false,
          walletBalance: wallet?.balance ?? 0
        }
      })
      this.loading = false
    }
  }
}
</script>

<style></style>
